/**
 * 최상위 Root Layout - 모든 Provider와 전역 설정을 포함
 * FSD Architecture: apps 레이어의 최상위 구성 요소
 */
import React, { Suspense } from 'react';
import { Outlet } from 'react-router';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { HeroUIProvider } from '@heroui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { common_loading } from '@paraglide/messages';
import ThemeProvider from '../../components/ThemeProvider';
import { AuthProvider } from '@edusense/shared-auth-core';
import { SessionProvider } from '@edusense/entities-auth';
import heroUITheme from '../../hero';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (React Query v5: cacheTime renamed to gcTime)
    },
  },
});

// VS Code 환경에서 테마 정보 가져오기
const getThemeConfig = () => {
  const isVSCodeEnv = typeof window !== 'undefined' && window.ide === 'vscode';
  const vscodeTheme = typeof window !== 'undefined' ? (window as any).vscodeTheme : null;
  const isVSCodeThemed = typeof window !== 'undefined' ? (window as any).isVSCodeThemed : false;
  
  return {
    initialTheme: isVSCodeEnv && vscodeTheme ? vscodeTheme : 'light',
    useVSCodeTheming: isVSCodeEnv && isVSCodeThemed,
  };
};

/**
 * 모든 Provider를 감싸는 최상위 레이아웃
 */
export const RootLayout: React.FC = () => {
  const { initialTheme, useVSCodeTheming } = getThemeConfig();
  
  return (
    <Suspense fallback={<div>{common_loading()}</div>}>
      <QueryClientProvider client={queryClient}>
        <HeroUIProvider
          theme={heroUITheme}
          disableAnimation={false}
          skipFramerMotionAnimations={false}
        >
          <ThemeProvider initialTheme={initialTheme} isVSCodeThemed={useVSCodeTheming}>
            <AuthProvider>
              <SessionProvider authServerUrl={import.meta.env.VITE_AUTH_SERVER_URL || 'http://localhost:8080'}>
                <Toaster position="bottom-center" toastOptions={{
                  duration: 3000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                }} />
                <HelmetProvider>
                  <Outlet />
                </HelmetProvider>
              </SessionProvider>
            </AuthProvider>
          </ThemeProvider>
        </HeroUIProvider>
      </QueryClientProvider>
    </Suspense>
  );
};

export default RootLayout;